import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity,
  CheckCircle2,
  ImageIcon,
  Inbox,
  Truck,
  Users as UsersIcon,
} from 'lucide-react';
import type { JobStatus } from '@godrop/types';
import { api, ApiError } from '../api';
import { Shell } from '../components/layout/Shell';
import { PageHeader } from '../components/layout/PageHeader';
import { StatCard } from '../components/StatCard';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { StatusChip } from '../components/ui/status-chip';
import { TrendChart, type TrendPoint } from '../components/charts/TrendChart';
import { StatusDonut, type DonutDatum } from '../components/charts/StatusDonut';
import { fmtMoney, fmtMoney2, fmtRelative } from '../lib/utils';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';

interface Stats {
  users_total: number;
  couriers_active_30m: number;
  jobs_by_status: Record<string, number>;
  completed_today: number;
  pod_uploaded_24h: number;
  avg_payout_7d: number;
  ratings_total: number;
  generated_at: string;
}

interface Activity {
  signups: Array<{
    id: string;
    phone: string;
    name: string | null;
    role: string;
    vehicle_mode: string | null;
    at: string;
  }>;
  posts: Array<{
    id: string;
    code: string;
    title: string;
    payout: number;
    vehicle_required: string;
    urgency: 'standard' | 'urgent';
    status: JobStatus;
    poster_phone: string;
    poster_name: string | null;
    at: string;
  }>;
  accepts: Array<{
    id: string;
    code: string;
    title: string;
    payout: number;
    courier_phone: string;
    courier_name: string | null;
    at: string;
  }>;
}

export function OverviewPage() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [trend, setTrend] = useState<TrendPoint[] | null>(null);
  const [activity, setActivity] = useState<Activity | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      api<Stats>('/api/admin/stats'),
      api<{ points: TrendPoint[] }>('/api/admin/stats/timeseries?days=14'),
      api<Activity>('/api/admin/activity?limit=10'),
    ])
      .then(([s, t, a]) => {
        setStats(s);
        setTrend(t.points);
        setActivity(a);
      })
      .catch((e) => setErr(e instanceof ApiError ? `Error ${e.status}` : 'Could not load'));
  }, []);

  const donut: DonutDatum[] = stats
    ? (['open', 'accepted', 'completed', 'cancelled', 'expired'] as const)
        .map((s) => ({ status: s, name: s, value: stats.jobs_by_status[s] ?? 0 }))
        .filter((d) => d.value > 0)
    : [];

  return (
    <Shell title="Overview" subtitle={stats ? `Snapshot · ${new Date(stats.generated_at).toLocaleTimeString('en-SG', { hour: '2-digit', minute: '2-digit' })}` : 'Loading…'}>
      <PageHeader
        title="Operational overview"
        description="Live snapshot of the GoDrop platform — users, drops, couriers."
      />

      {err && (
        <Card className="mb-6 border-error/40 bg-error-tint">
          <CardContent className="py-4 text-sm text-error-on-tint">{err}</CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        <StatCard
          label="Users"
          value={stats?.users_total ?? null}
          icon={UsersIcon}
          accent="brand"
        />
        <StatCard
          label="Active couriers"
          value={stats?.couriers_active_30m ?? null}
          hint="last 30 min"
          icon={Truck}
          accent="accepted"
        />
        <StatCard
          label="Open drops"
          value={stats ? (stats.jobs_by_status.open ?? 0) : null}
          icon={Inbox}
          accent="open"
        />
        <StatCard
          label="In-progress"
          value={stats ? (stats.jobs_by_status.accepted ?? 0) : null}
          icon={Activity}
          accent="accepted"
        />
        <StatCard
          label="Completed today"
          value={stats?.completed_today ?? null}
          icon={CheckCircle2}
          accent="completed"
        />
        <StatCard
          label="POD uploads"
          value={stats?.pod_uploaded_24h ?? null}
          hint="last 24h"
          icon={ImageIcon}
          accent="completed"
        />
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-start justify-between gap-3">
            <div>
              <CardTitle>Drops over time</CardTitle>
              <CardDescription>Last 14 days · created vs completed vs cancelled</CardDescription>
            </div>
            <Badge variant="brand" className="tnum">
              avg payout 7d {stats ? fmtMoney2(stats.avg_payout_7d) : '—'}
            </Badge>
          </CardHeader>
          <CardContent>
            {trend ? <TrendChart data={trend} /> : <Skeleton className="h-64 w-full" />}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Status mix</CardTitle>
            <CardDescription>All-time · {stats?.ratings_total ?? '—'} ratings</CardDescription>
          </CardHeader>
          <CardContent>
            {stats ? (
              donut.length > 0 ? (
                <StatusDonut data={donut} />
              ) : (
                <p className="py-6 text-center text-sm text-text-muted">No drops yet.</p>
              )
            ) : (
              <Skeleton className="h-44 w-full" />
            )}
          </CardContent>
        </Card>
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Recent drops</CardTitle>
              <CardDescription>Most recent posted</CardDescription>
            </div>
            <Button asChild variant="ghost" size="sm">
              <Link to="/jobs">View all</Link>
            </Button>
          </CardHeader>
          <CardContent className="px-0 pb-0">
            <ul className="divide-y divide-border">
              {!activity && Array.from({ length: 5 }).map((_, i) => (
                <li key={i} className="px-5 py-3"><Skeleton className="h-4 w-full" /></li>
              ))}
              {activity && activity.posts.length === 0 && (
                <li className="px-5 py-6 text-center text-sm text-text-muted">No drops yet.</li>
              )}
              {activity?.posts.slice(0, 8).map((p) => (
                <li key={p.id} className="px-5 py-3">
                  <Link to={`/jobs/${p.id}`} className="flex items-center gap-3 hover:text-brand-700 dark:hover:text-brand-500">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="tnum text-xs font-semibold text-text-muted">{p.code}</span>
                        <StatusChip status={p.status} />
                        {p.urgency === 'urgent' && <Badge variant="urgent">urgent</Badge>}
                      </div>
                      <p className="mt-0.5 truncate text-sm font-medium text-text">{p.title}</p>
                      <p className="truncate text-xs text-text-muted">
                        {p.poster_name ?? p.poster_phone} · {fmtRelative(p.at)}
                      </p>
                    </div>
                    <span className="tnum shrink-0 text-base font-bold text-brand-700 dark:text-brand-500">
                      {fmtMoney(p.payout)}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Recent signups</CardTitle>
              <CardDescription>Last accounts created</CardDescription>
            </div>
            <Button asChild variant="ghost" size="sm">
              <Link to="/users">View all</Link>
            </Button>
          </CardHeader>
          <CardContent className="px-0 pb-0">
            <ul className="divide-y divide-border">
              {!activity && Array.from({ length: 5 }).map((_, i) => (
                <li key={i} className="px-5 py-3"><Skeleton className="h-4 w-full" /></li>
              ))}
              {activity && activity.signups.length === 0 && (
                <li className="px-5 py-6 text-center text-sm text-text-muted">No users yet.</li>
              )}
              {activity?.signups.slice(0, 8).map((u) => (
                <li key={u.id} className="px-5 py-3">
                  <Link to={`/users/${u.id}`} className="flex items-center gap-3 hover:text-brand-700 dark:hover:text-brand-500">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-text">{u.name ?? '—'}</span>
                        <Badge>{u.role}</Badge>
                        {u.vehicle_mode && <Badge>{u.vehicle_mode}</Badge>}
                      </div>
                      <p className="tnum mt-0.5 truncate text-xs text-text-muted">
                        {u.phone} · {fmtRelative(u.at)}
                      </p>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>
    </Shell>
  );
}
